/**
 * cloudFunctions.ts
 *
 * Centrale helper voor het aanroepen van Firebase Cloud Functions vanuit de frontend.
 * API keys en secrets leven ALLEEN server-side — nooit in de browser-bundle.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase';

const functions = getFunctions(app, 'us-central1');

// ── Types ────────────────────────────────────────────────────────────────────

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface OpenAIChatParams {
  messages: ChatMessage[];
  model?: string;
  maxTokens?: number;
  temperature?: number;
  responseFormat?: 'json_object' | null;
}

export interface TTSParams {
  text: string;
  voice?: string;
  model?: string;
  speed?: number;
}

export interface ElevenLabsTTSParams {
  text: string;
  voiceId?: string;
  modelId?: string;
  stability?: number;
  similarityBoost?: number;
  style?: number;
  useSpeakerBoost?: boolean;
}

export interface GeminiChatParams {
  prompt: string;
  model?: string;
}

// ── OpenAI chat completions ──────────────────────────────────────────────────

export async function callOpenAIChat(params: OpenAIChatParams): Promise<string> {
  const fn = httpsCallable<OpenAIChatParams, { success: boolean; content: string }>(
    functions,
    'openAIChat'
  );
  const result = await fn(params);
  if (!result.data.success) throw new Error('openAIChat cloud function mislukt');
  return result.data.content;
}

// ── OpenAI TTS ───────────────────────────────────────────────────────────────

export async function callOpenAITTS(params: TTSParams): Promise<ArrayBuffer> {
  const fn = httpsCallable<TTSParams, { success: boolean; audio: string; mimeType: string }>(
    functions,
    'openAITTS'
  );
  const result = await fn(params);
  if (!result.data.success) throw new Error('openAITTS cloud function mislukt');

  // Decodeer base64 terug naar binary
  const binary = atob(result.data.audio);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// ── ElevenLabs TTS ──────────────────────────────────────────────────────────

export async function callElevenLabsTTS(params: ElevenLabsTTSParams): Promise<ArrayBuffer> {
  const fn = httpsCallable<ElevenLabsTTSParams, { success: boolean; audio: string; mimeType: string }>(
    functions,
    'elevenLabsTTS'
  );
  const result = await fn(params);
  if (!result.data.success) throw new Error('elevenLabsTTS cloud function mislukt');

  const binary = atob(result.data.audio);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// ── Google AI / Gemini ───────────────────────────────────────────────────────

export async function callGeminiChat(params: GeminiChatParams): Promise<string> {
  const fn = httpsCallable<GeminiChatParams, { success: boolean; content: string }>(
    functions,
    'geminiChat'
  );
  const result = await fn(params);
  if (!result.data.success) throw new Error('geminiChat cloud function mislukt');
  return result.data.content;
}
